import { useState } from 'react';
import { Check, Link2, Loader2, X } from 'lucide-react';
import { parseSteamId } from '../../../lib/steamId';
import { fetchPlayer } from '../../../lib/cs2kz';
import { joinMotw, leaveMotw } from '../../../database/mapOfTheWeek';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

// Opt-in / linking card. Resolves the user's stored steamLink when possible and
// falls back to a manual SteamID paste (e.g. for vanity /id/ URLs, which can't
// be resolved in the browser). Linking is once and for all — the id is kept
// until the player leaves.
const LinkKzProfile = ({ uid, username, steamLink, me, onChanged }) => {
  const [value, setValue] = useState(steamLink || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleLink = async (event) => {
    event.preventDefault();
    setError('');

    const parsed = parseSteamId(value);
    if (!parsed) {
      setError('Enter a valid Steam profile URL, SteamID64, or STEAM_ id.');
      return;
    }
    if (parsed.unresolvable) {
      setError(
        "A vanity URL (steamcommunity.com/id/…) can't be resolved automatically yet. Paste your SteamID64 or STEAM_1:0:… id instead — find it on steamid.io."
      );
      return;
    }

    setBusy(true);
    try {
      // Confirm the id exists in CS2KZ so we can store their canonical name.
      const player = await fetchPlayer(parsed.steamId);
      await joinMotw(uid, {
        steamId: parsed.steamId,
        username,
        kzPlayerName: player?.name || null,
      });
      onChanged();
    } catch {
      setError('Could not reach the CS2KZ API. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async () => {
    setBusy(true);
    try {
      await leaveMotw(uid);
      onChanged();
    } catch {
      setError('Could not update your link. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (me) {
    return (
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
          <div className="flex items-center gap-2 text-sm">
            <Check className="h-4 w-4 text-success" />
            <span className="text-muted-foreground">You&rsquo;re competing as</span>
            <span className="font-medium">{me.kzPlayerName || me.steamId}</span>
          </div>
          <Button variant="outline" size="sm" onClick={handleUnlink} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
            Leave
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Link2 className="h-4 w-4 text-primary" />
          Join the weekly race
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleLink} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="motw-steam">Steam profile URL or SteamID</Label>
            <Input
              id="motw-steam"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="https://steamcommunity.com/profiles/7656… or STEAM_1:0:…"
            />
            <p className="text-xs text-muted-foreground">
              Link this once. We keep it, and your runs during a session window land on the board
              automatically.
            </p>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" disabled={busy || !value.trim()}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            Link &amp; join
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default LinkKzProfile;
